import type { CSSProperties } from 'react';

import { Box, IconButton, alpha, styled } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

export const overflowPopperStyle: CSSProperties = {
  zIndex: 1300,
};

export const overflowPopperPaperSx: SxProps<Theme> = {
  mt: 1,
  borderRadius: '12px',
  maxWidth: '400px',
};

export const HeaderRoot = styled(Box)({
  display: 'flex',
  gap: '1rem',
  alignItems: 'stretch',
  width: '100%',
  marginBottom: '0.75rem',
  flexShrink: 0,
});

export const HeaderCard = styled(Box)<{ isModal?: boolean }>(({ isModal }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
  backgroundColor: '#FEFEFE',
  borderRadius: '0.875rem',
  boxShadow: isModal ? 'none' : '0 1px 8px 0 rgba(0, 0, 0, 0.15)',
}));

export const TitleCard = styled(HeaderCard)<{ hasBackButton?: boolean }>(({ hasBackButton }) => ({
  padding: hasBackButton ? '0.5rem 1rem 0.5rem 0.5rem' : '1rem 1.5rem',
  flexShrink: 0,
}));

export const FiltersCard = styled(HeaderCard)<{ isModal?: boolean }>(({ isModal }) => ({
  padding: isModal ? 0 : '0.625rem 1rem 0.625rem 1.5rem',
  flex: 1,
  minWidth: 0,
}));

export const CardContent = styled(Box)({
  display: 'flex',
  gap: '0.625rem',
  alignItems: 'center',
  width: '100%',
});

export const IconWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '1.25rem',
  height: '1.25rem',
  flexShrink: 0,
  color: '#615E6F',

  '& svg': {
    fontSize: '1.25rem',
  },
});

export const Label = styled('span')({
  fontFamily: 'Neue Frutiger World',
  fontSize: '0.875rem',
  fontWeight: 500,
  lineHeight: 1.5,
  color: '#615E6F',
  whiteSpace: 'nowrap',
});

export const CountText = styled('span')({
  fontFamily: 'Neue Frutiger World',
  fontSize: '0.875rem',
  fontWeight: 500,
  lineHeight: 1.5,
  color: '#615E6F',
  whiteSpace: 'nowrap',
});

export const FiltersContainer = styled(Box)({
  display: 'flex',
  flex: 1,
  gap: '0.625rem',
  alignItems: 'center',
  overflow: 'hidden',
  minWidth: 0,
});

export const FilterChipStyled = styled(Box)<{ clickable?: boolean }>(({ clickable, theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '2rem',
  padding: '0 0.625rem',
  borderRadius: '0.5rem',
  border: `1px solid ${theme.palette.accent.main}`,
  backgroundColor: 'transparent',
  flexShrink: 0,
  gap: '0.325rem',
  cursor: clickable ? 'pointer' : 'default',

  '&:hover': {
    backgroundColor: clickable ? 'rgba(142, 109, 225, 0.08)' : 'transparent',
  },
}));

export const FilterChipText = styled('span')(({ theme }) => ({
  fontFamily: 'Neue Frutiger World',
  fontSize: '0.875rem',
  fontWeight: 500,
  lineHeight: 1.5,
  color: theme.palette.accent.main,
  textAlign: 'center',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: 'flex',
  alignItems: 'center',
  maxWidth: '20rem',
}));

export const FilterChipValue = styled('span')(({ theme }) => ({
  fontFamily: 'Neue Frutiger World',
  fontSize: '0.875rem',
  fontWeight: 500,
  lineHeight: 1.5,
  color: theme.palette.accent.main,
  marginLeft: '0.25rem',
  marginBottom: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: 'inline-block',
  maxWidth: '12rem',
  verticalAlign: 'bottom',
}));

export const ViewMoreButton = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '2rem',
  padding: '0 0.625rem',
  borderRadius: '0.5rem',
  border: `1px solid ${theme.palette.accent.main}`,
  backgroundColor: alpha(theme.palette.accent.main, 0.08),
  flexShrink: 0,
  cursor: 'pointer',
  fontFamily: 'Neue Frutiger World',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: theme.palette.accent.main,
  whiteSpace: 'nowrap',
  userSelect: 'none',

  '&:hover': {
    backgroundColor: alpha(theme.palette.accent.main, 0.16),
  },
}));

export const OverflowFiltersContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  padding: '0.75rem',
});

export const RemoveButton = styled(IconButton)(({ theme }) => ({
  padding: '0.125rem',
  marginLeft: '0.125rem',
  marginRight: '-0.25rem',
  color: theme.palette.accent.main,

  '&:hover': {
    backgroundColor: alpha(theme.palette.accent.main, 0.08),
  },

  '& svg': {
    fontSize: '1rem',
  },
}));
